<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { EasePack } from 'gsap/EasePack'
import type { HeadingProps } from '~/components/text/Heading.vue'
import { useAppStore } from '~/stores/AppStore'
import { AppView } from '~/types'

defineProps<{
  data: SectionHomeRecord
  moduleIndex: number
  level: HeadingProps['level']
  isSemantic: boolean
  invertedStyles: Record<string, boolean | undefined>
}>()

const store = useAppStore()
const { isMobileOrTablet } = useDevice()
const isHoverable = ref(false)

defineEmits(['on-click'])

const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null
let tlIntro: GSAPTimeline | null = null
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let tlPush: GSAPTimeline | null = null
let tlReel: GSAPTimeline | null = null
let tweenHover: GSAPTween | null = null

const maskRef = ref<HTMLElement | null>(null)

/*
function showReel() {
  isHoverable.value = false
  if (tlReel) {
    tlReel.play()
    store.setView(AppView.VIDEO)
  }
}
*/

function hideReel() {
  if (tlReel) {
    tlReel.timeScale(2).reverse()
  }
}

const handleMouseEnter = () => {
  if (!isHoverable.value) return
  tweenHover?.timeScale(1).play()
}

const handleMouseLeave = () => {
  if (!isHoverable.value) return
  tweenHover?.timeScale(1.5).reverse()
}

onMounted(() => {
  if (ctxRef.value) {
    gsap.registerPlugin(ScrollTrigger, EasePack)

    ctx = gsap.context((self: gsap.Context) => {
      const elVideoHeading = self.selector ? self.selector('.fx-reel-heading') : null
      const elVideoTitle = self.selector ? self.selector('.fx-hero-title span span') : null
      const elVideoMask = self.selector ? self.selector('.fx-video-mask') : null
      const elVideoMaskPath = self.selector ? self.selector('.fx-mask-path') : null

      gsap.set(elVideoMask, { autoAlpha: 0 })
      gsap.set(elVideoHeading, { opacity: 1 })
      gsap.set(elVideoTitle, { y: '100%' })

      tlIntro = gsap
        .timeline({
          paused: true,
          onComplete: () => {
            isHoverable.value = true
          }
        })
        .addLabel('start')
        .to(elVideoTitle, {
          y: '0%',
          duration: 1.3,
          ease: 'power4.out'
        })
        .to(
          elVideoMask,
          {
            duration: 1.3,
            autoAlpha: 1,
            ease: 'power4.out'
          },
          'start'
        )

      ScrollTrigger.create({
        trigger: ctxRef.value,
        start: `${window.innerHeight * 2.2} bottom`,
        markers: false,
        onEnter: () => {
          tlIntro?.timeScale(1).play()
        },
        onEnterBack: () => {
          tlIntro?.timeScale(3.8).reverse()
        }
      })

      tlPush = gsap.timeline({
        scrollTrigger: {
          trigger: ctxRef.value,
          start: isMobileOrTablet
            ? `${window.innerHeight * 3.5} center`
            : `${window.innerHeight * 2.5} center`,
          end: isMobileOrTablet ? `+=${window.innerHeight * 1.5}` : `+=${window.innerHeight * 2}`,
          markers: false,
          scrub: isMobileOrTablet ? 0.5 : true
        },
        onStart: () => {
          isHoverable.value = false
        },
        onReverseComplete: () => {
          isHoverable.value = true
        }
      })

      const mm = gsap.matchMedia()
      mm.add('(min-width: 1024px)', () => {
        if (tlPush) {
          tlPush.to(ctxRef.value, {
            y: `-${window.innerHeight * 1.5}`
          })
        }
      })
      mm.add('(max-width: 1023px)', () => {
        if (tlPush) {
          tlPush.to(
            ctxRef.value,
            {
              opacity: 0
            },
            -1
          )
        }
      })
      gsap.defaults({ ease: 'power4.out' })

      tlReel = gsap.timeline({
        paused: true,
        onStart: () => {
          gsap.set(elVideoMask, { clearProps: 'all' })
        },
        onReverseComplete: () => {
          store.setView(AppView.APP)
          isHoverable.value = true
        }
      })
      tlReel.addLabel('start')
      tlReel.to(
        elVideoMaskPath,
        {
          duration: 3,
          scale: 12,
          rotate: -15,
          transformOrigin: 'center center',
          ease: 'linear'
        },
        'start'
      )
      tlReel.to(
        elVideoHeading,
        {
          duration: 1,
          opacity: 0
        },
        'start'
      )
      tlReel.to(
        elVideoMask,
        {
          duration: 1,
          width: '100%',
          height: '100%',
          y: 0,
          x: 0
        },
        'start'
      )

      tweenHover = gsap.to(elVideoMask, {
        duration: 0.4,
        scale: 1.05,
        ease: 'power1.out',
        paused: true
      })

      const xTo = gsap.quickTo(elVideoMask, 'x', { duration: 0.6, ease: 'power3' })
      const yTo = gsap.quickTo(elVideoMask, 'y', { duration: 0.6, ease: 'power3' })

      window.addEventListener('mousemove', (e) => {
        if (!isHoverable.value) return
        xTo((-(e.clientX - window.innerWidth / 2) / window.innerWidth) * 150)
        yTo((-(e.clientY - window.innerHeight / 2) / window.innerHeight) * 150)
      })
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div id="home-content" ref="ctxRef" class="relative text-black lg:text-center">
    <div
      class="from-tramsparent absolute -top-30 left-0 h-30 w-full bg-gradient-to-t from-white to-transparent"
    />
    <div>
      <div
        class="container relative h-screen w-full overflow-hidden lg:flex lg:flex-col lg:justify-center"
      >
        <Heading
          v-if="data.title"
          class="pt-28 font-black leading-[0.84] tracking-tighter clamp-[2.2rem,10.5vw,6.25rem] lg:mx-auto lg:pt-0 lg:clamp-[4rem,11vw,10rem]"
          :level="level"
          :size="undefined"
          :is-semantic="isSemantic"
        >
          <span class="fx-hero-title block" v-html="data.title" />
        </Heading>
        <VideoRocket
          ref="maskRef"
          :title="data.title"
          :src="data.reel?.videoLoop?.url"
          :src-full="data.reel?.video?.video?.streamingUrl"
          @on-click="$emit('on-click')"
          @on-ended="hideReel"
          @on-exit="hideReel"
          @on-mouse-enter="handleMouseEnter"
          @on-mouse-leave="handleMouseLeave"
        />
      </div>
    </div>
  </div>
</template>
